'use client';

import { InfoIcon, PlusTrans } from 'assets/icons';
import { CSSProperties, ReactNode, useContext, useEffect } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { useTranslations } from 'next-intl';
import { CustomImage as Image } from '..';
import { OffersModalNewContext } from 'components/shared/OffersModal';
import { OffersDataType } from './OffersCarousel';
import styles from './CarouselFooterCard.module.css';

export const CarouselFooterCard = ({
  className,
  data,
  bgColor = '#f8f8f8',
}: {
  className?: string;
  data: OffersDataType[];
  bgColor?: string;
}) => {
  const t = useTranslations('common');
  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      align: 'center',
      slidesToScroll: 1,
      skipSnaps: false,
      loop: true,
    },
    [Autoplay({ delay: 4000, stopOnInteraction: false })],
  );

  const OffersModalNewClient = useContext(OffersModalNewContext);

  const handleModal = ({ modal }: { modal?: ReactNode }) => {
    OffersModalNewClient.set(modal);
    OffersModalNewClient.show();
  };

  useEffect(() => {
    if (emblaApi) {
      const autoplay = emblaApi.plugins().autoplay as ReturnType<
        typeof Autoplay
      >;
      if (autoplay) {
        const onPointerDown = () => autoplay.stop();
        const onPointerUp = () => autoplay.play();

        emblaApi.on('pointerDown', onPointerDown);
        emblaApi.on('pointerUp', onPointerUp);

        return () => {
          emblaApi.off('pointerDown', onPointerDown);
          emblaApi.off('pointerUp', onPointerUp);
        };
      }
    }
  }, [emblaApi]);

  if (data.length < 1) return null;

  const renderItem = (item: OffersDataType, index: number) => {
    const clickHandler =
      item.onClick ??
      (item.modal ? () => handleModal({ modal: item.modal }) : undefined);

    return (
      <div
        className={styles.footerItemContent}
        key={index}
        onClick={clickHandler}
        style={
          {
            '--bg-color': bgColor,
            cursor: clickHandler ? 'pointer' : undefined,
          } as CSSProperties
        }
        role={clickHandler ? 'button' : undefined}
        tabIndex={clickHandler ? 0 : undefined}
        onKeyDown={
          clickHandler
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  clickHandler();
                }
              }
            : undefined
        }
      >
        {item.image && item.image.length > 0 ? (
          <div
            className={`${styles.imageContainer}${
              item.imageType === 'square' ? ` ${styles.square}` : ''
            }`}
          >
            {item.image.map((img, i) => (
              <Image src={img} alt="" height="24" width="24" key={i} />
            ))}
          </div>
        ) : null}
        <p>{item.title}</p>
        {item.onClick ? (
          <InfoIcon fill="rgba(0,0,0,0.3)" aria-hidden="true" />
        ) : null}
        {item.modal ? (
          <PlusTrans className={styles.plus} aria-hidden="true" />
        ) : null}
      </div>
    );
  };

  if (data.length === 1) {
    return (
      <div
        className={`${styles.root}${className ? ` ${className}` : ''}`}
        role="region"
        aria-label={t('carouselFooterCard.content.ariaLabel.offer')}
      >
        {renderItem(data[0], 0)}
      </div>
    );
  }

  return (
    <div
      className={`${styles.root}${className ? ` ${className}` : ''}`}
      role="region"
      aria-label={t('carouselFooterCard.content.ariaLabel.offers')}
    >
      <div className={styles.embla} ref={emblaRef}>
        <div className={styles.emblaContainer}>
          {data.map((item, index) => (
            <div className={styles.emblaSlide} key={index}>
              {renderItem(item, index)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
